import { ChangeEvent, FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useLocalStorageState from "use-local-storage-state";

import { EmailSuppressionRow } from "@signalist/shared-types";

import { faColumns3 } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import { TItem } from "@/components/SortableList/SortableList";
import { Table } from "@/components/Table/Table";
import { applyPersistedColumnConfig } from "@/components/Table/applyPersistedColumnConfig";
import {
	useGetEmailSuppressionsQuery,
	useRemoveEmailSuppressionMutation,
} from "@/pages/Private/redux/contact/contact-suppression.api";
import { Order } from "@/types/types";
import { showGlobalToast } from "@/utils/showGlobalToast";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { ContactsColumnModal } from "../../Contacts/components/ContactsColumnModal";
import { useContactsUrlState } from "../../Contacts/hooks/useContactsUrlState";
import { buildSuppressedColumns } from "../utils/suppressedColumns";
import { columnsToTableConfig, pageAfterRemoval } from "../utils/suppressionDisplay";
import { RemoveSuppressionModal } from "./RemoveSuppressionModal";

/**
 * The Suppressed tab (PREC-4266): the workspace's suppressed addresses, newest first, with Search,
 * Select columns and one Remove per row. A removal is confirmed first, runs once however often
 * Remove is clicked, and reports the address the server says it removed.
 */
export const SuppressedTable: FunctionComponent = () => {
	const { t } = useTranslation();

	const { page, setPage, limit, setLimit, searchValue, setSearchValue, query } =
		useContactsUrlState({
			prefix: "suppressed",
			defaultSortBy: "suppressedAt",
			defaultSortOrder: Order.DESC,
			withSearch: true,
		}) as ReturnType<typeof useContactsUrlState> & {
			searchValue: string;
			setSearchValue: (value: string) => void;
			query: string;
		};

	const { data: response, isFetching } = useGetEmailSuppressionsQuery({
		page,
		limit,
		searchValue: query || undefined,
	});
	const [removeSuppression] = useRemoveEmailSuppressionMutation();
	const removal = useSingleFlight();

	const [pending, setPending] = useState<EmailSuppressionRow | null>(null);
	const [showSelectColumns, setShowSelectColumns] = useState(false);
	const [tableConfig, setTableConfig] = useLocalStorageState<TItem[]>(
		"contacts-columns-suppressed-v1",
		{ defaultValue: [] }
	);

	const rows = useMemo(() => response?.data ?? [], [response]);
	const baseColumns = useMemo(() => buildSuppressedColumns(t, setPending), [t]);
	const columns = useMemo(
		() => applyPersistedColumnConfig(baseColumns, tableConfig),
		[baseColumns, tableConfig]
	);
	const effectiveTableConfig = tableConfig.length ? tableConfig : columnsToTableConfig(baseColumns);

	const confirmRemoval = useCallback(
		(row: EmailSuppressionRow) =>
			removal.run(async () => {
				try {
					const removed = await removeSuppression(row.id).unwrap();

					showGlobalToast(
						t("contacts.suppressed.removed", { email: removed.email }),
						6000,
						"success"
					);
					setPage(pageAfterRemoval(page, rows.length));
				} catch {
					showGlobalToast(t("contacts.suppressed.removeFailed", { email: row.email }));
				} finally {
					// Closed by Cancel meanwhile, or another row opened: leave that dialog alone.
					setPending((current) => (current?.id === row.id ? null : current));
				}
			}),
		[removal, removeSuppression, t, setPage, page, rows.length]
	);

	return (
		<div className="w-full">
			<div className="flex items-center gap-2 h-11 px-2 bg-white border-b border-border-light">
				<div className="w-[280px] flex-shrink-0">
					<InputField
						className="!rounded-[10px]"
						classNameWrapper="!mb-0"
						handleChange={(event: ChangeEvent<HTMLInputElement>) =>
							setSearchValue(event.target.value)
						}
						name="search"
						placeholder={t("contacts.searchPlaceholder")}
						size={ButtonSize.XS}
						value={searchValue}
					/>
				</div>
				<div className="ml-auto w-fit">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						image={<Icon className="mr-2" icon={faColumns3} />}
						size={ButtonSize.S}
						title={t("contacts.selectColumns")}
						onClick={() => setShowSelectColumns(true)}
					/>
				</div>
			</div>

			<Table
				columns={columns}
				data={rows}
				handleChangeRowsPerPage={setLimit}
				handleFetchPage={setPage}
				isLoading={isFetching}
				limit={limit}
				noDataText={t("contacts.suppressed.empty")}
				overallCount={response?.meta.itemCount}
				page={page}
				sticky
				tableConfig={effectiveTableConfig}
				totalPages={response?.meta.pageCount}
			/>

			{showSelectColumns && (
				<ContactsColumnModal
					handleResetToDefault={() => setTableConfig([])}
					setShowSelectColumns={setShowSelectColumns}
					setTableConfig={setTableConfig}
					showSelectColumns={showSelectColumns}
					tableConfig={effectiveTableConfig}
				/>
			)}

			{pending && (
				<RemoveSuppressionModal
					email={pending.email}
					isRemoving={removal.isRunning}
					onClose={() => setPending(null)}
					onConfirm={() => void confirmRemoval(pending)}
				/>
			)}
		</div>
	);
};
